import { useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { toKebabCase } from "common";
import { eventBus } from "common/event-bus";
import { hasRule, isEmptyFilter } from "common/query/filter";
import { getWeekdayName } from "common/date-time/common";
import type { UTCDateRange } from "common/date-time/types";
import type { Entity } from "common/entities/types";
import { SchedulerFilter } from "common/types/scheduler-filters";
import type { PaginationValue, QueryForEntity } from "common/query/types";
import type { Context } from "common/types/context";
import { CancellablePromise } from "common/types/promises";
import { LoadingIcon } from "common/widgets/loading-icon";
import { InjectedProps, toggleable } from "common/widgets/toggleable";
import {
  getAssignmentEntityFromWorkOrderEntity,
  getScrollHeight,
  hasWorkOrderById,
  removeWorkOrderById,
} from "./functions";
import {
  getAssignmentRule,
  getTotalCount,
  getWorkOrders,
} from "./functions/query";
import { WorkOrderTile } from "./tile";
import type { OnWorkOrderViewClick, WorkOrder } from "./types";

interface PropTypes {
  context: Context;
  label?: string;
  isDraggable: boolean;
  dateRange: UTCDateRange;
  filterQuery: SchedulerFilter;
  searchQuery: QueryForEntity;
  searchValue: string;
  onWorkOrderViewClick: OnWorkOrderViewClick;
  workOrderEntity: Entity;
  additionalInfo?: string;
  parentPanelHeight: number;
}

type Props = PropTypes & InjectedProps;

export function DayGroup({
  context,
  label,
  isDraggable,
  dateRange,
  filterQuery,
  searchQuery,
  searchValue,
  onWorkOrderViewClick,
  workOrderEntity,
  additionalInfo,
  parentPanelHeight,
  toggleFn,
  toggleOpened,
}: Props) {
  const [resultsCount, setResultsCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState<number | undefined>(undefined);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);

  const fetchDataRequest = useRef<CancellablePromise<unknown> | null>(null);

  const hasMore = nextPage !== undefined;

  const getWorkOrdersAndCount = (page: number, pageSize?: number) => {
    fetchDataRequest.current = CancellablePromise.all([
      getWorkOrders(
        context,
        workOrderEntity,
        dateRange,
        filterQuery,
        searchQuery,
        page,
        pageSize,
      ),
      searchQuery || filterQuery?.query?.filter
        ? getTotalCount(context, workOrderEntity, dateRange, filterQuery)
        : CancellablePromise.resolve(),
    ]);

    return fetchDataRequest.current;
  };

  const loadInitialWorkOrders = () => {
    getWorkOrdersAndCount(0).then(([paginationValue, count]) => {
      const resultsCount = paginationValue.fullCount ?? 0;

      unstable_batchedUpdates(() => {
        setNextPage(paginationValue.next ?? undefined);
        setResultsCount(resultsCount);
        setTotalPages(paginationValue.totalPages ?? 0);
        setTotalCount(count ?? resultsCount);
        setWorkOrders(paginationValue.data ?? []);
      });
    });
  };

  useEffect(() => {
    const schedulerReloadSubscription = eventBus.on("SchedulerReload", () => {
      loadInitialWorkOrders();
    });

    loadInitialWorkOrders();

    return () => {
      schedulerReloadSubscription.unsubscribe();
    };
  }, [
    context.site.name,
    dateRange,
    filterQuery,
    searchQuery,
    workOrderEntity?.name,
  ]);

  const needsAssignedWorkOrderRemoval = (assignedWorkOrderId: string) => {
    if (!hasWorkOrderById(workOrders, assignedWorkOrderId)) return false;

    return (
      !filterQuery?.query ||
      hasRule(
        filterQuery.query,
        getAssignmentRule(
          getAssignmentEntityFromWorkOrderEntity(context, workOrderEntity).name,
        ),
      )
    );
  };

  const removeAssignedWorkOrder = (assignedWorkOrderId: string) => {
    const newWorkOrders = removeWorkOrderById(workOrders, assignedWorkOrderId);

    if (hasMore) {
      getWorkOrdersAndCount(workOrders.length - 1, 1).then(
        ([paginationValue, count]) => {
          const updatedResultsCount =
            paginationValue.fullCount ?? resultsCount - 1;

          unstable_batchedUpdates(() => {
            setResultsCount(updatedResultsCount);
            setTotalCount(count ?? updatedResultsCount);
            setWorkOrders(newWorkOrders.concat(paginationValue.data ?? []));
          });
        },
      );
    } else {
      unstable_batchedUpdates(() => {
        setResultsCount((prev) => prev - 1);
        setTotalCount((prev) => prev - 1);
        setWorkOrders(newWorkOrders);
      });
    }
  };

  useEffect(() => {
    const assignWorkOrderSubscription = eventBus.on(
      "WorkOrderAssigned",
      (assignedWorkOrderId) => {
        if (needsAssignedWorkOrderRemoval(assignedWorkOrderId)) {
          removeAssignedWorkOrder(assignedWorkOrderId);
        }
      },
    );

    return () => {
      assignWorkOrderSubscription.unsubscribe();
    };
  }, [workOrders, filterQuery, workOrderEntity]);

  const getNextPage = (paginationValue: PaginationValue<WorkOrder>) => {
    return paginationValue.next === nextPage ? undefined : paginationValue.next;
  };

  const loadNextWorkOrders = () => {
    if (nextPage === undefined) return;

    getWorkOrdersAndCount(nextPage).then(([paginationValue, count]) => {
      const updatedResultsCount = paginationValue.fullCount ?? resultsCount;

      unstable_batchedUpdates(() => {
        setResultsCount(updatedResultsCount);
        setTotalPages(paginationValue.totalPages ?? totalPages);
        setNextPage(getNextPage(paginationValue));
        setTotalCount(count ?? updatedResultsCount);
        setWorkOrders(workOrders.concat(paginationValue.data ?? []));
      });
    });
  };

  const countLabel =
    searchValue || !isEmptyFilter(filterQuery?.query?.filter)
      ? _("{RESULTS} of {TOTAL}")
          .replace("{RESULTS}", `${resultsCount}`)
          .replace("{TOTAL}", `${totalCount}`)
      : resultsCount;

  const labelText = label ?? getWeekdayName(dateRange.from);
  const containerClassName = `x-work-order-group x-work-order-group-${toKebabCase(labelText)}`;
  const qaContainerClassName = `qa${containerClassName.slice(1)}`;

  return (
    <div className={`${containerClassName} ${qaContainerClassName}`}>
      <button
        type="button"
        className={`x-work-order-day-title ${additionalInfo ? "x-overdue" : ""} ${
          toggleFn ? "x-pointer" : ""
        }`}
        onClick={toggleFn}
      >
        <h2>
          <span className="x-text-uppercase">{labelText}</span>
          {` (${countLabel})`}
        </h2>
        {toggleFn && (
          <i
            className={`fa ${toggleOpened ? "fa-chevron-down" : "fa-chevron-up"}`}
          />
        )}
      </button>
      {additionalInfo && toggleOpened ? (
        <div className="x-overdue-info">{additionalInfo}</div>
      ) : undefined}
      {(!toggleFn || toggleOpened) &&
        (workOrders.length ? (
          <InfiniteScroll
            next={loadNextWorkOrders}
            hasMore={hasMore}
            loader={<LoadingIcon />}
            dataLength={workOrders.length}
            height={getScrollHeight(parentPanelHeight, resultsCount)}
          >
            {workOrders.map((wo) => (
              <WorkOrderTile
                key={wo.SYSTEM_id}
                workOrder={wo}
                draggable={isDraggable}
                context={context}
                filterQuery={filterQuery}
                searchValue={searchValue}
                onClick={onWorkOrderViewClick}
              />
            ))}
          </InfiniteScroll>
        ) : (
          <div className="x-work-order-message">{_("No planned work")}</div>
        ))}
    </div>
  );
}

export const ToggleableDayGroup = toggleable({ defaultOpened: false })(
  DayGroup,
);
